import { supabase } from '@/lib/supabase'

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}

export type PushStatus = 'idle' | 'subscribed' | 'unsubscribed' | 'blocked' | 'unsupported'

function withTimeout<T>(p: Promise<T>, ms = 8000): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    p.then(v => { clearTimeout(t); resolve(v) }, e => { clearTimeout(t); reject(e) })
  })
}

export async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    // Evita travar indefinidamente em iOS logo após instalar
    const reg = await withTimeout(navigator.serviceWorker.ready, 8000).catch(async () => {
      try { return await navigator.serviceWorker.getRegistration() } catch { return null }
    })
    return reg || null
  } catch { return null }
}

export function isPwaInstalled(): boolean {
  const isStandalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator as any).standalone === true
  return !!isStandalone
}

export function hasController(): boolean {
  try { return !!navigator?.serviceWorker?.controller } catch { return false }
}

export async function ensureSwActiveAndControlled(timeoutMs = 20000): Promise<'ok'|'reload_needed'|'failed'> {
  const start = Date.now()
  const reg = await getRegistration()
  if (!reg) return 'failed'
  const sw = reg.installing || reg.waiting || reg.active || null
  const waitState = async () => new Promise<void>((resolve) => {
    if (!sw) return resolve()
    if ((sw as any).state === 'active') return resolve()
    const onStateChange = () => { if ((sw as any).state === 'active') { sw.removeEventListener('statechange', onStateChange as any); resolve() } }
    sw.addEventListener('statechange', onStateChange as any)
    setTimeout(() => { try { sw.removeEventListener('statechange', onStateChange as any) } catch {} ; resolve() }, Math.max(2000, timeoutMs/2))
  })
  await waitState()
  if (hasController()) return 'ok'
  // Aguarda controllerchange por mais alguns segundos
  const awaitController = async () => new Promise<void>((resolve) => {
    const onCtrl = () => { navigator.serviceWorker.removeEventListener('controllerchange', onCtrl); resolve() }
    navigator.serviceWorker.addEventListener('controllerchange', onCtrl)
    setTimeout(() => { try { navigator.serviceWorker.removeEventListener('controllerchange', onCtrl) } catch {} ; resolve() }, Math.max(3000, timeoutMs - (Date.now()-start)))
  })
  await awaitController()
  return hasController() ? 'ok' : 'reload_needed'
}

export function getVapidPublicKey(): string | null {
  const vapidKey = (import.meta as any).env?.VITE_WEBPUSH_VAPID_PUBLIC_KEY || (window as any).__VAPID_PUBLIC_KEY__ || null
  return vapidKey || null
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  const reg = await getRegistration()
  if (!reg || !('pushManager' in reg)) return null
  try { return await reg.pushManager.getSubscription() } catch { return null }
}

export async function subscribePush(userId: string, role: string | null): Promise<PushSubscription | null> {
  if (!('Notification' in window)) return null
  if (!('serviceWorker' in navigator)) return null
  const reg = await getRegistration()
  if (!reg) return null
  // Safari/iOS exige suporte explícito ao PushManager
  // Alguns navegadores mais antigos podem não expor pushManager
  // @ts-ignore
  if (!('pushManager' in reg)) return null
  if (Notification.permission === 'denied') return null

  let existing: PushSubscription | null = null
  try { existing = await reg.pushManager.getSubscription() } catch (e) { console.error('[push] getSubscription error', e) }
  const vapidKey = (import.meta as any).env?.VITE_WEBPUSH_VAPID_PUBLIC_KEY || (window as any).__VAPID_PUBLIC_KEY__
  if (!vapidKey) { console.error('[push] VAPID public key ausente'); return null }

  let sub: PushSubscription | null = existing
  if (!sub) {
    const key = urlBase64ToUint8Array(String(vapidKey))
    const attempt = async () => await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }), 10000)
    try {
      // pequenos retries para iOS pós-permissão
      for (let i = 0; i < 3 && !sub; i++) {
        try { sub = await attempt() } catch (e) { await new Promise(r => setTimeout(r, 600 * (i + 1))) }
      }
    } catch (e) { console.error('[push] subscribe fatal', e) }
    if (!sub) return null
  }

  try {
    const info = JSON.parse(JSON.stringify(sub))
    const endpoint: string = info.endpoint
    const p256dh: string = info.keys?.p256dh
    const auth: string = info.keys?.auth
    const platform = navigator.platform || ''
    const ua = navigator.userAgent || ''
    const { error } = await supabase.from('webpush_subscriptions').upsert({
      user_id: userId,
      endpoint,
      p256dh,
      auth,
      platform,
      ua,
      role_snapshot: role || null,
      active: true,
      last_seen_at: new Date().toISOString(),
    }, { onConflict: 'endpoint' })
    if (error) console.error('[push] persist error', error)
    // Evita duplicidade no MESMO dispositivo: desativa inscrições antigas com a mesma UA para este usuário
    try {
      await supabase.from('webpush_subscriptions')
        .update({ active: false, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('ua', ua)
        .neq('endpoint', endpoint)
    } catch {}
  } catch (e) { console.error('[push] persist exception', e) }

  return sub
}

export async function showLocalTestNotification(): Promise<boolean> {
  try {
    const reg = await getRegistration()
    if (!reg) return false
    await reg.showNotification('Cripto Alert (local)', { body: 'Teste local de banner', data: { url: '/app' } })
    return true
  } catch (e) {
    console.error('[push] local notify error', e)
    return false
  }
}

export async function getDiagnostics(): Promise<{ pwaInstalled: boolean; permission: NotificationPermission | 'unsupported'; controller: boolean; swState: string | null; vapidOk: boolean }>{
  const reg = await getRegistration()
  const sw = reg && (reg.installing || reg.waiting || reg.active)
  return {
    pwaInstalled: isPwaInstalled(),
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    controller: hasController(),
    swState: sw ? (sw as any).state || null : null,
    vapidOk: !!getVapidPublicKey(),
  }
}

export async function isPushEnabledForAll(): Promise<boolean> {
  try {
    const { supabase } = await import('@/lib/supabase')
    const { data, error } = await supabase.from('app_settings').select('value').eq('key', 'ops.webpush_enabled_for_all').maybeSingle()
    if (error) return false
    const v = (data?.value as string) || 'false'
    return v === 'true'
  } catch { return false }
}

export async function getResetEpoch(): Promise<number | null> {
  try {
    const { data, error } = await supabase.from('app_settings').select('value').eq('key', 'ops.webpush_reset_epoch').maybeSingle()
    if (error) return null
    const v = Number((data?.value as any) || '')
    return Number.isFinite(v) ? v : null
  } catch { return null }
}

export async function unsubscribePush(userId?: string | null): Promise<boolean> {
  const reg = await getRegistration()
  if (!reg) return false
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return true
  try {
    const info = JSON.parse(JSON.stringify(sub))
    const endpoint: string = info.endpoint
    await supabase.from('webpush_subscriptions').update({ active: false, last_seen_at: new Date().toISOString() }).eq('endpoint', endpoint)
  } catch {}
  try { return await sub.unsubscribe() } catch { return false }
}

export async function ensureRenewed(userId: string, role: string | null): Promise<PushStatus> {
  if (!('Notification' in window)) return 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'
  const reg = await getRegistration()
  if (!reg) return 'unsupported'
  const existing = await reg.pushManager.getSubscription()
  if (!existing && Notification.permission === 'granted') {
    const sub = await subscribePush(userId, role)
    return sub ? 'subscribed' : 'unsubscribed'
  }
  // Reconciliar com o servidor: se existir localmente porém estiver inativo no DB, reativar
  if (existing) {
    try {
      const info = JSON.parse(JSON.stringify(existing))
      const endpoint: string = info.endpoint
      const p256dh: string = info.keys?.p256dh
      const auth: string = info.keys?.auth
      const platform = navigator.platform || ''
      const ua = navigator.userAgent || ''
      const { data } = await supabase.from('webpush_subscriptions').select('id,active').eq('endpoint', endpoint).maybeSingle()
      if (!data || (data as any).active !== true) {
        await supabase.from('webpush_subscriptions').upsert({
          user_id: userId,
          endpoint,
          p256dh,
          auth,
          platform,
          ua,
          role_snapshot: role || null,
          active: true,
          last_seen_at: new Date().toISOString(),
        } as any, { onConflict: 'endpoint' })
      } else {
        try { await supabase.from('webpush_subscriptions').update({ last_seen_at: new Date().toISOString() }).eq('id', (data as any).id) } catch {}
      }
    } catch (e) { console.error('[push] ensureRenewed reconcile error', e) }
  }
  return existing ? 'subscribed' : 'unsubscribed'
}
